import React from "react";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const PrivacyPolicyScreen = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { isAuthenticated } = useAuth();

	// Check if user came from within the app
	const cameFromApp = location.state?.from === "settings";

	const handleBack = () => {
		if (cameFromApp && isAuthenticated) {
			navigate(-1); // Go back if came from settings
		} else {
			navigate("/login"); // Go to login if accessed directly
		}
	};

	const renderBackButton = () => {
		if (!isAuthenticated && !cameFromApp) {
			return (
				<Link
					to="/login"
					className="flex items-center space-x-1 sm:space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 text-gray-600 dark:text-gray-400"
				>
					<ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
					<span className="font-medium text-xs sm:text-base">Login</span>
				</Link>
			);
		}

		return (
			<button
				onClick={handleBack}
				className="mr-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors duration-200"
			>
				<ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 dark:text-gray-400" />
			</button>
		);
	};

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
			<div className="container mx-auto max-w-3xl px-4">
				<div className="mb-6 flex items-center relative">
					<div className="absolute left-0">{renderBackButton()}</div>
					<h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex-1 text-center">
						My-Cron Privacy Policy
					</h1>
				</div>

				<div className="bg-white dark:bg-gray-800/50 rounded-lg shadow-sm">
					<div className="divide-y divide-gray-100 dark:divide-gray-700/50">
						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								Information We Collect
							</h2>
							<p className="text-gray-600 dark:text-gray-300 mb-3">
								We collect and process the following Google user data:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
								<li>
									Google Account Information:
									<ul className="list-circle pl-5 mt-1 space-y-1">
										<li>Basic profile data (name, email, profile picture)</li>
										<li>
											Calendar data including events, schedules, and calendar
											list information
										</li>
									</ul>
								</li>
							</ul>
						</section>

						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								How We Use Your Information
							</h2>
							<p className="text-gray-600 dark:text-gray-300 mb-3">
								Your Google user data is used exclusively for:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
								<li>
									Creating and maintaining your personalized profile within our
									application
								</li>
								<li>
									Calendar Management:
									<ul className="list-circle pl-5 mt-1 space-y-1">
										<li>Displaying and managing your calendar events</li>
										<li>
											Processing calendar data to provide scheduling
											functionality
										</li>
										<li>Enabling event creation, modification, and deletion</li>
									</ul>
								</li>
							</ul>
						</section>

						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								Data Sharing and Third Parties
							</h2>
							<p className="text-gray-600 dark:text-gray-300 mb-4">
								We do not share, sell, rent, or transfer your Google user data
								to third parties for commercial purposes. Your data is processed
								solely to provide our core scheduling and calendar optimization
								services:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
								<li>
									Calendar data is processed through secure artificial
									intelligence services (such as OpenAI) with strict privacy
									policies, solely for the purpose of providing intelligent
									scheduling assistance and calendar optimization
								</li>
								<li>
									This AI processing is a fundamental part of our core service
									functionality and is never used for advertising, marketing, or
									other commercial purposes outside of providing our primary
									service
								</li>
								<li>
									Your profile information is only used for account management
									within our service
								</li>
								<li>
									All third-party processing is conducted under strict data
									protection agreements and is limited to only what is necessary
									for providing our core calendar management features
								</li>
							</ul>
						</section>

						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								Data Retention and Deletion
							</h2>
							<p className="text-gray-600 dark:text-gray-300 mb-3">
								We handle your Google user data with the following retention and
								deletion policies:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
								<li>
									Your Google user data is retained only as long as you maintain
									an active account with our service
								</li>
								<li>
									You can request deletion of your Google user data at any time
									by contacting us
								</li>
								<li>
									Upon account deletion, all your Google user data is
									permanently removed from our systems within 30 days
								</li>
								<li>
									You can revoke our application's access to your Google data at
									any time through your Google Account settings
								</li>
								<li>
									We maintain logs of calendar operations for 90 days for
									security and debugging purposes, after which they are
									automatically deleted
								</li>
							</ul>
						</section>

						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								Data Security
							</h2>
							<p className="text-gray-600 dark:text-gray-300 mb-4">
								We protect your Google user data through:
							</p>
							<ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
								<li>
									Industry-standard encryption for data in transit and at rest
								</li>
								<li>Secure storage in Google Cloud Platform services</li>
								<li>Regular security audits and updates</li>
								<li>Strict access controls and monitoring</li>
							</ul>
						</section>

						<section className="p-6">
							<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
								Contact Us
							</h2>
							<p className="text-gray-600 dark:text-gray-300">
								For any questions about this Privacy Policy or to request
								deletion of your data, please contact us at:{" "}
								<a
									href="mailto:rjbulcher@gmail.com"
									className="text-blue-500 dark:text-blue-400 hover:underline"
								>
									rjbulcher@gmail.com
								</a>
							</p>
						</section>

						<div className="p-6">
							<p className="text-sm text-gray-500 dark:text-gray-400">
								Last updated: November 4, 2024
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicyScreen;
